import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { DocumentPreview } from "./documents/DocumentPreview";
import { DocumentList } from "./documents/DocumentList";

interface Document {
  id: string;
  name: string;
  size: number;
  type: string;
  path: string;
  created_at: string;
}

interface ClientDocumentsProps {
  documents: Document[];
  clientId: number;
}

export const ClientDocuments = ({ documents: initialDocuments, clientId }: ClientDocumentsProps) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [documents, setDocuments] = useState<Document[]>(initialDocuments);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchClientDocuments();
  }, [clientId]);

  const fetchClientDocuments = async () => {
    try {
      console.log("Fetching documents for client:", clientId);
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching documents:', error);
        throw error;
      }

      console.log("Fetched documents:", data);
      setDocuments(data || []);
      setLoading(false);
    } catch (error: any) {
      console.error('Error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch documents: " + error.message,
      });
      setLoading(false);
    }
  };

  const handlePreview = async (path: string) => {
    try {
      const { data: { publicUrl } } = await supabase.storage
        .from('documents')
        .getPublicUrl(path);

      setPreviewUrl(publicUrl);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to preview document: " + error.message,
      });
    }
  };

  const handleDownload = async (path: string, fileName: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('documents')
        .download(path);

      if (error) throw error;

      const url = window.URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to download document: " + error.message,
      });
    }
  };

  const handleDelete = async (id: string, path: string) => {
    try {
      const { error: storageError } = await supabase.storage
        .from('documents')
        .remove([path]);

      if (storageError) throw storageError;

      const { error: dbError } = await supabase
        .from('documents')
        .delete()
        .eq('id', id);

      if (dbError) throw dbError;

      toast({
        title: "Success",
        description: "Document deleted successfully",
      });

      fetchClientDocuments();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete document: " + error.message,
      });
    }
  };

  if (loading) {
    return (
      <Card className="p-6 bg-[#222222] border-white/10">
        <div className="text-center py-8 text-gray-400">
          Loading documents...
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 bg-[#222222] border-white/10">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-white">Documents</h2>
          <Button
            onClick={() => document.getElementById('file-upload')?.click()}
            className="flex items-center gap-2"
          >
            <Upload className="h-4 w-4" />
            Upload Document
          </Button>
          <input
            type="file"
            id="file-upload"
            className="hidden"
            onChange={(e) => {
              const files = e.target.files;
              if (files && files.length > 0) {
                const event = new CustomEvent('filesSelected', { 
                  detail: {
                    files: Array.from(files),
                    clientId: clientId
                  }
                });
                window.dispatchEvent(event);
              }
            }}
          />
        </div>

        <DocumentList
          documents={documents}
          onPreview={handlePreview}
          onDownload={handleDownload}
          onDelete={handleDelete}
        />

        <DocumentPreview 
          previewUrl={previewUrl} 
          onClose={() => setPreviewUrl(null)} 
        />
      </div>
    </Card>
  );
};